/**
 * Waypoint Panel - POE-style teleport destination selection UI
 * Acts on left sidebar, zones for selected act on right.
 * Only activated waypoints allow teleportation.
 */

import { getWaypointManager, WaypointZoneInfo, WAYPOINT_ZONES } from '@shared/world/WaypointManager';
import { ZoneType, ZONES } from '@shared/world/Zone';
import { getAudioManager } from '@client/audio/AudioManager';
import { t } from '@locales/index';

interface ActInfo {
  id: number;
  name: string;
  zones: ZoneType[];
}

const ACTS: ActInfo[] = [
  {
    id: 1,
    name: 'act1',
    zones: ['otag_koyu', 'karanlik_orman', 'ruh_batakligi', 'erlik_tapinagi'],
  },
  {
    id: 2,
    name: 'act2',
    zones: ['ergenekon_girisi', 'firtina_ovasi', 'ates_magarasi', 'yersub_tahti'],
  },
  {
    id: 3,
    name: 'act3',
    zones: ['karanlik_tunel', 'olum_nehri', 'kemik_ormani', 'lanetli_saray'],
  },
  {
    id: 4,
    name: 'act4',
    zones: ['erlik_kapisi', 'cehennem_bahcesi', 'taht_odasi'],
  },
];

export class WaypointPanel {
  private visible: boolean = false;
  private currentZone: ZoneType | null = null;
  private selectedAct: number = 0; // index into ACTS
  private hoveredZoneIndex: number = -1;
  private hoveredActIndex: number = -1;
  private scrollOffset: number = 0;

  private panelWidth: number = 550;
  private panelHeight: number = 450;
  private screenWidth: number;
  private screenHeight: number;

  // Layout constants
  private readonly ACT_SIDEBAR_WIDTH = 130;
  private readonly TITLE_HEIGHT = 45;
  private readonly FOOTER_HEIGHT = 35;

  public onTeleport?: (destination: ZoneType) => void;
  public onClose?: () => void;

  constructor(screenWidth: number, screenHeight: number) {
    this.screenWidth = screenWidth;
    this.screenHeight = screenHeight;
    this.updatePanelSize();
  }

  private updatePanelSize(): void {
    this.panelWidth = Math.min(550, this.screenWidth - 40);
    this.panelHeight = Math.min(450, this.screenHeight - 80);
  }

  open(currentZone?: ZoneType): void {
    this.currentZone = currentZone || null;
    this.visible = true;
    getAudioManager().play('ui_open');
    this.hoveredZoneIndex = -1;
    this.hoveredActIndex = -1;
    this.scrollOffset = 0;

    // Auto-select the act of the current zone
    if (currentZone) {
      for (let i = 0; i < ACTS.length; i++) {
        if (ACTS[i].zones.includes(currentZone)) {
          this.selectedAct = i;
          break;
        }
      }
    }
  }

  close(): void {
    this.visible = false;
    getAudioManager().play('ui_close');
    if (this.onClose) {
      this.onClose();
    }
  }

  isOpen(): boolean {
    return this.visible;
  }

  resize(screenWidth: number, screenHeight: number): void {
    this.screenWidth = screenWidth;
    this.screenHeight = screenHeight;
    this.updatePanelSize();
  }

  update(_deltaTime: number): void {}

  private drawRoundedRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + w - r, y);
    ctx.quadraticCurveTo(x + w, y, x + w, y + r);
    ctx.lineTo(x + w, y + h - r);
    ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
    ctx.lineTo(x + r, y + h);
    ctx.quadraticCurveTo(x, y + h, x, y + h - r);
    ctx.lineTo(x, y + r);
    ctx.quadraticCurveTo(x, y, x + r, y);
    ctx.closePath();
  }

  private getZoneInfo(zoneId: ZoneType): WaypointZoneInfo | null {
    const zone = ZONES[zoneId];
    const wpInfo = WAYPOINT_ZONES[zoneId];
    if (!zone) return null;
    return {
      zoneId,
      name: zone.name,
      hasWaypoint: wpInfo?.hasWaypoint ?? false,
      isTown: wpInfo?.isTown ?? false,
      levelRange: zone.levelRange,
      activated: getWaypointManager().isWaypointActivated(zoneId),
    };
  }

  render(ctx: CanvasRenderingContext2D): void {
    if (!this.visible) return;

    const panelX = (this.screenWidth - this.panelWidth) / 2;
    const panelY = (this.screenHeight - this.panelHeight) / 2;

    // Dim background
    ctx.fillStyle = 'rgba(0, 0, 0, 0.75)';
    ctx.fillRect(0, 0, this.screenWidth, this.screenHeight);

    // Panel background
    ctx.fillStyle = '#12100e';
    this.drawRoundedRect(ctx, panelX, panelY, this.panelWidth, this.panelHeight, 8);
    ctx.fill();

    // Panel border
    ctx.strokeStyle = '#3d3228';
    ctx.lineWidth = 2;
    ctx.stroke();

    // === Title bar ===
    ctx.fillStyle = '#1e1a15';
    this.drawRoundedRect(ctx, panelX, panelY, this.panelWidth, this.TITLE_HEIGHT, 8);
    ctx.fill();
    // Bottom corners of title should be square
    ctx.fillRect(panelX, panelY + this.TITLE_HEIGHT - 8, this.panelWidth, 8);

    ctx.fillStyle = '#d4af37';
    ctx.font = 'bold 18px Arial';
    ctx.textAlign = 'center';
    ctx.fillText(t('waypoint.panelTitle'), panelX + this.panelWidth / 2, panelY + 30);

    // Close button (X)
    const btnSize = 25;
    const btnX = panelX + this.panelWidth - btnSize - 10;
    const btnY = panelY + 10;
    ctx.fillStyle = '#2a2218';
    this.drawRoundedRect(ctx, btnX, btnY, btnSize, btnSize, 5);
    ctx.fill();
    ctx.strokeStyle = '#8b7355';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(btnX + 7, btnY + 7);
    ctx.lineTo(btnX + 18, btnY + 18);
    ctx.moveTo(btnX + 18, btnY + 7);
    ctx.lineTo(btnX + 7, btnY + 18);
    ctx.stroke();

    // === Act sidebar (left) ===
    const sidebarX = panelX;
    const sidebarY = panelY + this.TITLE_HEIGHT;
    const sidebarH = this.panelHeight - this.TITLE_HEIGHT - this.FOOTER_HEIGHT;

    // Sidebar background
    ctx.fillStyle = '#18150f';
    ctx.fillRect(sidebarX, sidebarY, this.ACT_SIDEBAR_WIDTH, sidebarH);

    // Separator line
    ctx.strokeStyle = '#3d3228';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(sidebarX + this.ACT_SIDEBAR_WIDTH, sidebarY);
    ctx.lineTo(sidebarX + this.ACT_SIDEBAR_WIDTH, sidebarY + sidebarH);
    ctx.stroke();

    const actItemHeight = 55;
    for (let i = 0; i < ACTS.length; i++) {
      const act = ACTS[i];
      const ay = sidebarY + i * actItemHeight;
      const isSelected = i === this.selectedAct;
      const isHovered = i === this.hoveredActIndex;

      // Act tab background
      if (isSelected) {
        ctx.fillStyle = '#2a2218';
        ctx.fillRect(sidebarX, ay, this.ACT_SIDEBAR_WIDTH, actItemHeight);
        // Active indicator bar
        ctx.fillStyle = '#d4af37';
        ctx.fillRect(sidebarX, ay, 3, actItemHeight);
      } else if (isHovered) {
        ctx.fillStyle = '#1e1a14';
        ctx.fillRect(sidebarX, ay, this.ACT_SIDEBAR_WIDTH, actItemHeight);
      }

      // Act number
      ctx.fillStyle = isSelected ? '#d4af37' : (isHovered ? '#c0a050' : '#706050');
      ctx.font = `bold 14px Arial`;
      ctx.textAlign = 'center';
      ctx.fillText(
        t('waypoint.actLabel', { num: act.id }),
        sidebarX + this.ACT_SIDEBAR_WIDTH / 2,
        ay + 25
      );

      // Activated count
      const manager = getWaypointManager();
      const activatedCount = act.zones.filter(z => manager.isWaypointActivated(z)).length;
      const waypointCount = act.zones.filter(z => WAYPOINT_ZONES[z]?.hasWaypoint).length;
      ctx.fillStyle = isSelected ? '#9a8a60' : '#504a3a';
      ctx.font = '11px Arial';
      ctx.fillText(`${activatedCount}/${waypointCount}`, sidebarX + this.ACT_SIDEBAR_WIDTH / 2, ay + 42);

      // Separator between acts
      if (i < ACTS.length - 1) {
        ctx.strokeStyle = '#2a2520';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(sidebarX + 10, ay + actItemHeight);
        ctx.lineTo(sidebarX + this.ACT_SIDEBAR_WIDTH - 10, ay + actItemHeight);
        ctx.stroke();
      }
    }

    // === Zone list (right side) ===
    const listX = panelX + this.ACT_SIDEBAR_WIDTH + 15;
    const listY = sidebarY + 10;
    const listW = this.panelWidth - this.ACT_SIDEBAR_WIDTH - 30;
    const listH = sidebarH - 20;
    const zoneItemHeight = 55;

    const act = ACTS[this.selectedAct];
    if (!act) return;

    // Clip zone list
    ctx.save();
    ctx.beginPath();
    ctx.rect(listX - 5, listY, listW + 10, listH);
    ctx.clip();

    for (let i = 0; i < act.zones.length; i++) {
      const zoneId = act.zones[i];
      const info = this.getZoneInfo(zoneId);
      if (!info) continue;

      const zy = listY + (i - this.scrollOffset) * zoneItemHeight;
      if (zy + zoneItemHeight < listY || zy > listY + listH) continue;

      const isHovered = i === this.hoveredZoneIndex;
      const isCurrent = zoneId === this.currentZone;
      const isActivated = info.activated;
      const hasWP = info.hasWaypoint;

      // Zone item background
      if (isCurrent) {
        ctx.fillStyle = '#1a3a1a';
      } else if (isHovered && isActivated) {
        ctx.fillStyle = '#22201a';
      } else {
        ctx.fillStyle = '#151310';
      }
      this.drawRoundedRect(ctx, listX, zy + 2, listW, zoneItemHeight - 4, 5);
      ctx.fill();

      // Border
      if (isCurrent) {
        ctx.strokeStyle = '#2ecc71';
        ctx.lineWidth = 2;
      } else if (isHovered && isActivated) {
        ctx.strokeStyle = '#d4af37';
        ctx.lineWidth = 1;
      } else {
        ctx.strokeStyle = '#2a2520';
        ctx.lineWidth = 1;
      }
      ctx.stroke();

      // Waypoint icon (left side) - circle indicator
      const iconX = listX + 20;
      const iconY = zy + zoneItemHeight / 2;
      if (hasWP) {
        // Draw waypoint crystal icon
        ctx.beginPath();
        ctx.arc(iconX, iconY, 8, 0, Math.PI * 2);
        if (isActivated) {
          ctx.fillStyle = '#64b4ff';
          ctx.fill();
          // Inner glow
          ctx.beginPath();
          ctx.arc(iconX, iconY, 4, 0, Math.PI * 2);
          ctx.fillStyle = '#aadcff';
          ctx.fill();
        } else {
          ctx.strokeStyle = '#444';
          ctx.lineWidth = 2;
          ctx.stroke();
          // Lock symbol
          ctx.fillStyle = '#444';
          ctx.font = '10px Arial';
          ctx.textAlign = 'center';
          ctx.fillText('?', iconX, iconY + 4);
        }
      } else {
        // No waypoint - dash
        ctx.strokeStyle = '#333';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(iconX - 5, iconY);
        ctx.lineTo(iconX + 5, iconY);
        ctx.stroke();
      }

      // Zone name
      const nameX = listX + 40;
      if (isActivated) {
        ctx.fillStyle = isCurrent ? '#2ecc71' : (isHovered ? '#fff' : '#c0b8a0');
        ctx.font = `${isCurrent ? 'bold ' : ''}14px Arial`;
      } else {
        ctx.fillStyle = '#504a3a';
        ctx.font = '14px Arial';
      }
      ctx.textAlign = 'left';
      ctx.fillText(info.name, nameX, zy + 22);

      // Level range
      ctx.fillStyle = isActivated ? '#706858' : '#3a3530';
      ctx.font = '11px Arial';
      ctx.fillText(
        t('waypoint.levelRange', { min: info.levelRange.min, max: info.levelRange.max }),
        nameX,
        zy + 40
      );

      // Town badge
      if (info.isTown) {
        ctx.fillStyle = '#2ecc71';
        ctx.font = 'bold 10px Arial';
        ctx.textAlign = 'right';
        ctx.fillText(t('waypoint.town'), listX + listW - 10, zy + 22);
      }

      // Current zone indicator
      if (isCurrent) {
        ctx.fillStyle = '#2ecc71';
        ctx.font = 'bold 10px Arial';
        ctx.textAlign = 'right';
        ctx.fillText(t('waypoint.currentZone'), listX + listW - 10, zy + 40);
      }

      // Locked text for non-activated waypoint zones
      if (hasWP && !isActivated) {
        ctx.fillStyle = '#4a4030';
        ctx.font = '10px Arial';
        ctx.textAlign = 'right';
        ctx.fillText(t('waypoint.locked'), listX + listW - 10, zy + 32);
      }
    }

    ctx.restore();

    // Scroll indicators
    if (this.scrollOffset > 0) {
      ctx.fillStyle = '#d4af37';
      ctx.font = '14px Arial';
      ctx.textAlign = 'center';
      ctx.fillText('\u25B2', panelX + this.ACT_SIDEBAR_WIDTH + (this.panelWidth - this.ACT_SIDEBAR_WIDTH) / 2, listY + 12);
    }

    const maxVisible = Math.floor(listH / zoneItemHeight);
    if (this.scrollOffset + maxVisible < act.zones.length) {
      ctx.fillStyle = '#d4af37';
      ctx.font = '14px Arial';
      ctx.textAlign = 'center';
      ctx.fillText('\u25BC', panelX + this.ACT_SIDEBAR_WIDTH + (this.panelWidth - this.ACT_SIDEBAR_WIDTH) / 2, listY + listH - 5);
    }

    // === Footer ===
    const footerY = panelY + this.panelHeight - this.FOOTER_HEIGHT;
    ctx.strokeStyle = '#2a2520';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(panelX + 10, footerY);
    ctx.lineTo(panelX + this.panelWidth - 10, footerY);
    ctx.stroke();

    ctx.fillStyle = '#504a3a';
    ctx.font = '11px Arial';
    ctx.textAlign = 'center';
    ctx.fillText(t('waypoint.instructions'), panelX + this.panelWidth / 2, footerY + 20);
  }

  handleMouseEvent(e: { type: string; x: number; y: number; button: string; deltaY?: number }): boolean {
    if (!this.visible) return false;

    const panelX = (this.screenWidth - this.panelWidth) / 2;
    const panelY = (this.screenHeight - this.panelHeight) / 2;

    // Close button
    if (e.type === 'down' && e.button === 'left') {
      const btnSize = 25;
      const btnX = panelX + this.panelWidth - btnSize - 10;
      const btnY = panelY + 10;

      if (e.x >= btnX && e.x <= btnX + btnSize && e.y >= btnY && e.y <= btnY + btnSize) {
        this.close();
        return true;
      }

      // Click outside panel
      if (e.x < panelX || e.x > panelX + this.panelWidth ||
          e.y < panelY || e.y > panelY + this.panelHeight) {
        this.close();
        return true;
      }
    }

    // Act sidebar interaction
    const sidebarX = panelX;
    const sidebarY = panelY + this.TITLE_HEIGHT;
    const sidebarH = this.panelHeight - this.TITLE_HEIGHT - this.FOOTER_HEIGHT;
    const actItemHeight = 55;

    if (e.x >= sidebarX && e.x <= sidebarX + this.ACT_SIDEBAR_WIDTH &&
        e.y >= sidebarY && e.y <= sidebarY + sidebarH) {
      const actIndex = Math.floor((e.y - sidebarY) / actItemHeight);
      if (actIndex >= 0 && actIndex < ACTS.length) {
        if (e.type === 'move') {
          this.hoveredActIndex = actIndex;
          this.hoveredZoneIndex = -1;
        }
        if (e.type === 'down' && e.button === 'left') {
          this.selectedAct = actIndex;
          this.scrollOffset = 0;
          this.hoveredZoneIndex = -1;
          return true;
        }
      }
    } else {
      if (e.type === 'move') {
        this.hoveredActIndex = -1;
      }
    }

    // Zone list interaction
    const listX = panelX + this.ACT_SIDEBAR_WIDTH + 15;
    const listY = sidebarY + 10;
    const listW = this.panelWidth - this.ACT_SIDEBAR_WIDTH - 30;
    const listH = sidebarH - 20;
    const zoneItemHeight = 55;

    if (e.x >= listX && e.x <= listX + listW &&
        e.y >= listY && e.y <= listY + listH) {
      const relY = e.y - listY;
      const index = Math.floor(relY / zoneItemHeight) + this.scrollOffset;
      const act = ACTS[this.selectedAct];

      if (act && index >= 0 && index < act.zones.length) {
        if (e.type === 'move') {
          this.hoveredZoneIndex = index;
        }
        if (e.type === 'down' && e.button === 'left') {
          const zoneId = act.zones[index];
          const info = this.getZoneInfo(zoneId);
          // Only teleport to activated waypoints that aren't current zone
          if (info && info.activated && zoneId !== this.currentZone) {
            if (this.onTeleport) {
              this.onTeleport(zoneId);
            }
            this.close();
          }
          return true;
        }
      }
    } else {
      if (e.type === 'move') {
        this.hoveredZoneIndex = -1;
      }
    }

    // Scroll
    if (e.type === 'wheel' && e.deltaY !== undefined) {
      const act = ACTS[this.selectedAct];
      if (act) {
        const maxScroll = Math.max(0, act.zones.length - Math.floor(listH / zoneItemHeight));
        this.scrollOffset = Math.max(0, Math.min(maxScroll, this.scrollOffset + (e.deltaY > 0 ? 1 : -1)));
      }
      return true;
    }

    return true; // Consume all events when panel is open
  }

  handleKeyEvent(e: KeyboardEvent): boolean {
    if (!this.visible) return false;

    if (e.code === 'Escape') {
      this.close();
      return true;
    }

    return false;
  }
}
